import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {IListConfigContext} from '@src/@interfaces/contexts/IListConfigContext';
import {StatusField} from './StatusField';
import CommentsList from '../../CommentsList';
import {UserContext} from '@src/contexts/UserContext';
import app from '@src/helpers/app';
import {useQueryClient} from '@tanstack/react-query';
import LoaderOnOverlay from '@src/components/LoaderOnOverlay/LoaderOnOverlay';
import ExcuseForm from '../../ExcuseForm';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import ListOfFiles from '@views/orders/OrderFilesList/ListOfFiles';
import {IOrderProductFileModel} from '@src/@interfaces/models/Order/IOrderProductFileModel';
import {ProductIndependentField} from '../ProductField';
import {ProductPriceField} from '../ProductPriceField';
import {DepartmentsSelect} from '../DepartmentsSelect';
import {QuantityIndependentField} from '../QuantityField';
import {TotalPriceIndependentField} from '../TotalPriceField';
import {DescriptionField} from './DescriptionField';
import OrderProductCalendar from './OrderProductCalendar';
import Translate from '@src/components/Translate/Translate';
import {columnsTDProps} from '../OrderProductsField';
import {CustomPriceField} from '../CustomPriceField';
import useFormValue from '@src/hooks/useFormValue';
import {LocaleContext} from '@src/contexts/LocaleContext';
import trans from '@src/helpers/trans';
import OrderProductDetails from '@views/orders/fields/products/extra_row/OrderProductDetails';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import Portal from '@src/components/Portal/Portal';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import {PreviewIndependentField} from '@views/orders/fields/products/PreviewField';
import usePermissions from '@src/hooks/usePermissions';
import FrozePriceField from '@views/orders/fields/products/extra_row/FrozePriceField';

const minHeight500px = {minHeight: '500px'} as React.CSSProperties;

export default function OrderProductsExtraRow<Model extends IEmptyModel>(props: {
    orderId?: string | number | null,
    model: IOrderModel,
    rowRecord: Model,
    rowIndex: number,
    context?: IListConfigContext,
    controllerName?: string,
    rowId: string | number,
    collapsedRows?: Record<string | number, boolean>,
    setCollapsedRows?: React.Dispatch<React.SetStateAction<Record<string | number, boolean>>>,
    isDisabled?: boolean,
    listOfFilesUploaderContextControllerName?: string,
    onRefreshListOfFiles?: () => void,
    onExcuseSpecified?: () => void,
}) {
    const userContext = useContext(UserContext);
    const {locale} = useContext(LocaleContext);
    const [designContext, setDesignContext] = useContext(FormFieldDesignContext);
    const [wasDisabled] = useState(designContext.fieldsAreDisabled);

    const ref = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const productFormRef = useRef<HTMLDivElement>(null);
    const [displayOverlay, setDisplayOverlay] = useState(false);
    const [shouldIgnore, setShouldIgnore] = useState(false);
    const {currentValue: currentProductId} = useFormValue(`products.${props.rowIndex}.product_id`);
    const {modelOptions} = useContext(FormFieldItemPropsContext) ?? {};
    const {can, cant} = usePermissions();

    const displayExcuseForm = useCallback(() => setShouldIgnore(false), []);

    const orderId = props.orderId;
    const orderProductId = props.rowRecord.id;
    const modelExists = Boolean(orderProductId && orderId);
    const controllerName = props.context?.controllerName ?? props.controllerName ?? 'Orders';

    const canIgnoreAskingForDeadlineExpirationReason = can(
            'orders@can-ignore-asking-for-order-product-deadline-expiration-reason'
    );

    const user = app('auth').getUser();
    const product = props.model.products?.[props.rowIndex];
    const productDepartmentId = product?.department_id;
    const userBelongsToProductDepartment = user?.departments?.some(
            item => item.id === productDepartmentId
    ) ?? false;
    const shouldAskDeadlineReason = product?.reason_of_deadline_expiration_is_required === true;

    const isAllowedToLeaveReasonOfIncident = shouldAskDeadlineReason && (
            can('orders@can-leave-reason-of-order-product-deadline-expiration-unconditionally') ||
            userBelongsToProductDepartment && can(
                    'orders@can-leave-reason-of-order-product-deadline-expiration'
            )
    );

    const shouldDisplayDeadlineExpirationReasonForm = (
            shouldAskDeadlineReason && ref.current != null && controllerName != null && displayOverlay && !shouldIgnore
    );

    const cannotEditCompletedProducts = cant('orders@can-edit-completed-products');
    const cannotIgnoreDeadlineExpirationReason = cant('can-ignore-asking-for-order-product-deadline-expiration-reason');

    const currentProductData = props.model?.products?.[props.rowIndex];
    const thisRowMustBeDisabled = (
            cannotEditCompletedProducts && currentProductData?.is_completed === true ||
            cannotIgnoreDeadlineExpirationReason && currentProductData?.reason_of_deadline_expiration_is_required === true
    );

    useEffect(() => {
        if (!thisRowMustBeDisabled && (!displayOverlay || canIgnoreAskingForDeadlineExpirationReason)) {
            return;
        }

        setDesignContext(was => ({...was, fieldsAreDisabled: true}));

        return () => {
            setDesignContext(was => ({...was, fieldsAreDisabled: wasDisabled}));
        };
    }, [canIgnoreAskingForDeadlineExpirationReason, displayOverlay, setDesignContext, thisRowMustBeDisabled, wasDisabled]);

    useEffect(() => {
        setDisplayOverlay(shouldAskDeadlineReason);
    }, [shouldAskDeadlineReason]);

    const queryClient = useQueryClient();
    const queryKey = useMemo(() => app('queryKeys').getCustomQueryOfExactModelQueryKey(
            controllerName,
            orderId,
            'comments-list',
            `order-product-${orderProductId}`,
    ), [controllerName, orderId, orderProductId]);

    useEffect(() => {
        queryClient.invalidateQueries({queryKey, exact: true}).then();
    }, [queryClient, queryKey]);

    const isExpanded = !props.collapsedRows?.[props.rowId];
    const [signalSent, setSignalSent] = useState(false);
    useEffect(() => {
        if (signalSent || !isExpanded || !orderProductId) {
            return;
        }

        setSignalSent(true);
        app('http').route('orders.products.mark-as-viewed', {
            id: String(orderId),
            product: String(orderProductId),
        }, {
            headers: {'content-type': 'application/json'},
            body: {'controller-name': controllerName}
        }).then();
    }, [controllerName, isExpanded, orderId, orderProductId, signalSent]);

    const productIdNotSelected = !currentProductId;

    const files: IOrderProductFileModel[] = props.model?.products?.[props.rowIndex]?.files ?? [];

    const fieldHaveToBeDisabled = thisRowMustBeDisabled || productIdNotSelected || props.isDisabled;

    return <LoaderOnOverlay
            innerRef={ref}
            contentContainerRef={containerRef}
            forceDisplay={shouldDisplayDeadlineExpirationReasonForm}
    >
        <Portal
                container={containerRef?.current}
                display={displayOverlay}
        >
            <div className="floating-button__wrapper">
                <button
                        type="button"
                        className="floating-button"
                        onClick={displayExcuseForm}
                >
                    <i className="fa fa-exclamation-triangle"/>
                </button>
            </div>
        </Portal>

        <div className="row" style={shouldDisplayDeadlineExpirationReasonForm ? minHeight500px : undefined}>
            <Portal container={productFormRef.current} display={!currentProductId}>
                <div className="text-muted text-center py-5 px-2">
                    <Translate message="Forms.choose_field_option" replace={{
                        field: trans('OrderForm.products.fields.product', undefined, locale)
                    }}/>
                </div>
            </Portal>

            {shouldAskDeadlineReason ? (
                    !isAllowedToLeaveReasonOfIncident ?
                            <Portal container={ref.current}>
                                <div>
                                    <div className="mb-2">
                                        <div className="d-flex justify-content-end align-items-center">
                                            <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setShouldIgnore(true)}
                                            >
                                                <i className="fa fa-times"/>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="alert alert-danger px-3 py-2">
                                        <i className="fa fa-exclamation-triangle me-2"/>

                                        <Translate message={
                                                'OrderForm.products.deadline_expired.'
                                                + 'you-cannot-manage-order-product-because-of-'
                                                + 'missing-reason-of-deadline-expiration'
                                        }/>
                                    </div>
                                </div>
                            </Portal> :
                            <ExcuseForm
                                    portal={ref}
                                    controllerName={controllerName}
                                    modelId={String(orderId)}
                                    productId={String(orderProductId)}
                                    sendToRouteName="orders.products.specify-product-deadline-expiration-reason"
                                    onIgnore={setShouldIgnore}
                                    onSpecify={props.onExcuseSpecified}
                                    alertMessage="OrderForm.products.deadline_expired.why-did-it-happened-message"
                            />
            ) : null}

            <div className="col-sm-12 col-md-4">
                <ProductIndependentField rowIndex={props.rowIndex}
                                         isDisabled={thisRowMustBeDisabled || props.isDisabled}/>

                <LoaderOnOverlay
                        innerRef={productFormRef}
                        forceDisplay={productFormRef.current != null && !currentProductId}
                >
                    {/*<BoundProductField rowIndex={props.rowIndex} isDisabled={fieldHaveToBeDisabled}/>*/}

                    <div>
                        <div className="row mt-2">
                            <div className="col-auto d-flex align-items-center">
                                <Translate message="OrderForm.products.fields.product_price"/>:
                                <ProductPriceField rowIndex={props.rowIndex}/>
                            </div>
                            <div className="col d-flex justify-content-end align-items-center">
                                <Translate message="OrderForm.products.fields.quantity" className="me-2"/>
                                <div style={columnsTDProps['quantity'].style}>
                                    <QuantityIndependentField
                                            rowIndex={props.rowIndex}
                                            isDisabled={fieldHaveToBeDisabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-2">
                            <div className="float-end d-flex">
                                <Translate message="OrderForm.products.fields.total_price"/>:
                                <TotalPriceIndependentField rowIndex={props.rowIndex}/>
                            </div>
                            <CustomPriceField
                                    rowIndex={props.rowIndex}
                                    isDisabled={fieldHaveToBeDisabled}
                            />
                            <FrozePriceField
                                    rowIndex={props.rowIndex}
                                    isDisabled={fieldHaveToBeDisabled}
                                    className="mt-3"
                            />
                        </div>
                    </div>

                    <div className="my-3 border-top border-bottom">
                        <div className="row pt-2 pb-3">
                            <div className="col col-md-6">
                                <StatusField
                                        rowIndex={props.rowIndex}
                                        isDisabled={fieldHaveToBeDisabled}
                                />
                            </div>
                            <div className="col col-md-6">
                                <DepartmentsSelect
                                        rowIndex={props.rowIndex}
                                        isDisabled={fieldHaveToBeDisabled}
                                />
                            </div>
                            <div className="col-12 mt-2">
                                <DescriptionField
                                        rowIndex={props.rowIndex}
                                        isDisabled={fieldHaveToBeDisabled}
                                        rows={10}
                                />
                            </div>
                        </div>
                    </div>

                    <ListOfFiles
                            files={files}
                            orderId={orderId}
                            productId={orderProductId}
                            productCode={props.rowRecord.unique_code}
                            uploaderContextControllerName={props.listOfFilesUploaderContextControllerName}
                            disabled={fieldHaveToBeDisabled}
                            onRefresh={props.onRefreshListOfFiles}
                    />
                </LoaderOnOverlay>
            </div>
            <div className="col-sm-12 col-md-4">
                <CommentsList
                        label="OrderForm.products.fields.order-product-comments"
                        queryCacheKey={queryKey}
                        labelClassName="pt-0"
                        modelId={orderId}
                        extraRequestRouteUrlParams={{product: String(orderProductId)}}
                        controllerName={controllerName}
                        listenToModelName="OrderProduct"
                        listenToModelId={orderProductId ?? undefined}
                        isDisabled={
                                !modelExists ||
                                !userContext ||
                                userContext.cant('orders@can-leave-comments-to-order-product')
                        }
                        getCommentsRouteName="orders.products.comments.index"
                        createCommentRouteName="orders.products.comments.store"
                        isLazy={true}
                        shouldLoadNow={modelExists && (!props.collapsedRows || !props.collapsedRows[props.rowId])}
                        queryOptions={{staleTime: 5 * 60 * 1000}}
                        maxHeight={500}
                />
            </div>
            <div className="col">
                <PreviewIndependentField rowIndex={props.rowIndex} isDisabled={props.isDisabled} model={props.model}/>
                <OrderProductDetails model={props.model} modelOptions={modelOptions}/>
                <OrderProductCalendar rowIndex={props.rowIndex}/>
            </div>
        </div>
    </LoaderOnOverlay>;
}
